/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import Moment from "moment"; // Npm: moment.js library.
import _ from "underscore"; // Npm: underscore library.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.

import {
  Box,
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import Modal from "components/Modal";
import VendorUpsert from "components/VendorUpsert";
import VendorDelete from "components/VendorDelete";
import VendorDropDownOptions from "components/VendorDropDownOptions";
import BilateralRelation from "components/BilateralRelation";
import CreditManagementUpsert from "components/VendorCreditManagementUpsert";
import VendorPasswordChange from "components/VendorPasswordChange";

/*
 * GRAPHS
 */
import VendorReadQuery from "./__query__/index.vendor.read.query";

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags =
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    );

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const [sortModel, setSortModel] = React.useState([]);

  const {
    onOpen: onVendorCreateOpen,
    isOpen: isVendorCreateOpen,
    onClose: onVendorCreateClose,
  } = useDisclosure();
  const {
    onOpen: onVendorUpdateOpen,
    isOpen: isVendorUpdateOpen,
    onClose: onVendorUpdateClose,
  } = useDisclosure();
  const {
    onOpen: onVendorDeleteOpen,
    isOpen: isVendorDeleteOpen,
    onClose: onVendorDeleteClose,
  } = useDisclosure();
  const {
    onOpen: onCreditManagementUpsertOpen,
    isOpen: isCreditManagementUpsertOpen,
    onClose: onCreditManagementUpsertClose,
  } = useDisclosure();
  const {
    onOpen: onBilateralRelationOpen,
    isOpen: isBilateralRelationOpen,
    onClose: onBilateralRelationClose,
  } = useDisclosure();
  const {
    onOpen: onVendorPasswordChangeOpen,
    isOpen: isVendorPasswordChangeOpen,
    onClose: onVendorPasswordChangeClose,
  } = useDisclosure();

  React.useEffect(
    () => () => {
      _setFilterDebounced.cancel?.();
    },
    [_setFilterDebounced],
  );

  // Data assignment.
  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  const _QueryVendorReadVariables = React.useMemo(() => ({
    skip: paginationModel?.pageSize * paginationModel?.page,
    take: paginationModel?.pageSize,
    filter: _appliedFilter,
  }), [_appliedFilter, paginationModel?.page, paginationModel?.pageSize]);

  const _QueryVendorRead = useQuery(VendorReadQuery, {
    variables: _QueryVendorReadVariables,
    fetchPolicy: _isPreviousPageRef.current ?
      "cache-first" :
      Object.React.App.fetchPolicy,
  });

  const { refetch } = _QueryVendorRead;
  const _isFirstLoadCompleted = React.useRef(false);
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Data assignment.
  if (
    !_QueryVendorRead.loading &&
    0 < _QueryVendorRead.data?.VendorRead?.length
  )
    _isFirstLoadCompleted.current = true;
  if (
    0 === _QueryVendorRead?.data?.VendorRead?.length ||
    (0 < _QueryVendorRead?.data?.VendorRead?.length &&
      !_.every(_.pluck(_QueryVendorRead?.data?.VendorRead, "status"), (j) =>
        _successFlags.includes(j),
      ))
  )
    _isFirstLoadCompleted.current = false;

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);
  const _HandleFilterModelChange = React.useCallback((__filterModel) => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = { ...current, page: 0 };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
    _setFilterDebounced(__filterModel);
  }, [_setFilterDebounced]);

  // Data assignment.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.NO",
        align: "center",
        headerAlign: "center",
        width: 80,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
      },
      {
        field: "id",
        description: "ID of the vendor.",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        headerAlign: "center",
      },
      {
        field: "displayName",
        headerName: "Name",
        description: "Name of vendor.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "email",
        headerName: "Email",
        description: "Email of vendor.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "phone",
        headerName: "Phone",
        description: "Phone number of vendor.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "createdAt",
        headerName: "Created At",
        description: "Creation date of vendor.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        description: "Last update date of vendor.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "action",
        headerName: "Action",
        description: "Actions available for the vendor.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <VendorDropDownOptions
            vendorId={row?.id}
            currency={row?.Billing?.currency}
            onVendorPasswordChange={onVendorPasswordChangeOpen}
            onVendorUpdate={onVendorUpdateOpen}
            onVendorDelete={onVendorDeleteOpen}
            onBilateralRelationAttach={onBilateralRelationOpen}
            onCreditManagementUpsert={onCreditManagementUpsertOpen}
          />
        ),
      },
    ],
    [],
  );

  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryVendorRead?.data?.VendorRead?.map((r, index) =>
          "READ_SUCCESSFUL" === r.status
            ? {
                ...r,
                id:
                  r?.id ??
                  `vendor-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                createdAt: r?.createdAt ? r.createdAt.split("T")[0] : "-",
                updatedAt: r?.updatedAt ? r.updatedAt.split("T")[0] : "-",
                displayName: r?.displayName || "-",
                email: r?.email || "-",
                phone: r?.phone || "-",
              }
            : null,
        ),
      ),
    [
      _QueryVendorRead?.data?.VendorRead,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const _QueryVendorReadRowCount = _.first(_QueryVendorRead?.data?.VendorRead)?._totalCount;
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryVendorReadRowCount && null !== _QueryVendorReadRowCount)
      _rowCountRef.current = _QueryVendorReadRowCount;

    return _rowCountRef.current;
  }, [_QueryVendorReadRowCount]);

  // Return component.
  return (
    <>
      <Flex className="vendor base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          height="calc(100vh - 120px)"
          minH={0}
          maxH="calc(100vh - 120px)"
          overflow="hidden"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <Tooltip label="Create new vendor." fontSize="sm">
              <Button
                w="170px"
                color="rgb(92, 55, 255)"
                fontFamily="DM Sans"
                fontWeight="500"
                fontSize="16px"
                borderRadius="10px"
                onClick={onVendorCreateOpen}
                leftIcon={<IoMdAdd />}
                className="addButton"
              >
                New Vendor
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Button
                aria-label="Refresh"
                borderRadius="8px"
                fontWeight="450"
                minW={_isCurrentViewMobile ? "40px" : "20px"}
                w={_isCurrentViewMobile ? "40px" : void 0}
                px={_isCurrentViewMobile ? "0" : "16px"}
                fontSize={_isCurrentViewMobile ? "13px" : "16px"}
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={_isCurrentViewMobile ? void 0 : <HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                {_isCurrentViewMobile ? <HiArrowPath /> : "Refresh"}
              </Button>
              <DownloadToExcel
                cellsData={_QueryVendorRead?.data?.VendorRead?.map(
                  (item, __index) =>
                    _successFlags.includes(item.status)
                      ? {
                          "S.No.": `${__index + paginationModel.page * paginationModel.pageSize + 1}.`,
                          Name: item?.displayName ?? "-",
                          Email: item?.email ?? "-",
                          Phone: item?.phone ?? "-",
                          createdAt: item?.createdAt
                            ? item.createdAt.split("T")[0]
                            : "-",
                          updatedAt: item?.updatedAt
                            ? item.updatedAt.split("T")[0]
                            : "-",
                          "Created By": item?.Account?.displayName ?? "-",
                        }
                      : {},
                )}
                headersData={[
                  "S.No.",
                  "Name",
                  "Email",
                  "Phone",
                  "CreatedAt",
                  "UpdatedAt",
                  "Created By",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          {/* MUI */}
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            filterMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            sortingMode="client"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryVendorRead?.loading}
            disableColumnMenu={false}
          />
        </Flex>
        <Modal
          size="lg"
          title="Vendor Create"
          isOpen={isVendorCreateOpen}
          onClose={onVendorCreateClose}
          isCentered={false}
        >
          <VendorUpsert isCreateOnly={true} refetch={refetch} />
        </Modal>
        <Modal
          size="lg"
          title="Vendor Update"
          isOpen={isVendorUpdateOpen}
          onClose={onVendorUpdateClose}
          isCentered={false}
        >
          <VendorUpsert refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Vendor Delete"
          isOpen={isVendorDeleteOpen}
          onClose={onVendorDeleteClose}
        >
          <VendorDelete refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Bilateral Relation"
          isOpen={isBilateralRelationOpen}
          onClose={onBilateralRelationClose}
        >
          <BilateralRelation refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Payment Management"
          isOpen={isCreditManagementUpsertOpen}
          onClose={onCreditManagementUpsertClose}
        >
          <CreditManagementUpsert refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Vendor Password Change"
          isOpen={isVendorPasswordChangeOpen}
          onClose={onVendorPasswordChangeClose}
        >
          <VendorPasswordChange refetch={refetch} />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORTS
 */
export default Index;
